import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
    Chip,
    CircularProgress,
    Tab,
    Tabs,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { useTimezone } from '../../contexts/timezone'
import {
    RecordsTimelineEvent,
    RecordsTimelineKind,
    RecordsUnitOccupant,
    useRecordsUnitFile,
    useRecordsUnitTimeline,
} from '../../hooks/useRecords'
import { Routes } from '../../navigation/config/routes'
import {
    mergeRecordsSearch,
    parseRecordsSearch,
    parseUnitFileTab,
    recordsPersonPath,
    UnitFileTab,
} from './paths'
import { TrailEventCard, TrailEventList } from './TrailEventCard'
import { TrailExport } from './TrailExport'
import { TrailKindFilter } from './TrailKindFilter'
import {
    eventMatchesKindKeys,
    parseKindKeys,
    serializeAuditExportKinds,
    serializeKindKeys,
} from './trailKinds'

interface UnitFileProps {
    unitId: number
}

const KIND_FOR_TAB: Record<UnitFileTab, RecordsTimelineKind[] | null> = {
    timeline: null,
    leases: ['lease'],
    requests: ['service_request'],
    inspections: ['inspection', 'move_in_inspection'],
    work: ['workorder'],
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
    },
    header: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        flexShrink: 0,
        textAlign: 'left',
    },
    unitName: {
        fontWeight: 700,
    },
    unitPath: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    unitMeta: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
    },
    occupants: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1.5),
    },
    tabs: {
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        minHeight: 44,
    },
    tab: {
        minHeight: 44,
        textTransform: 'none',
        fontWeight: 600,
    },
    listWrap: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: theme.spacing(1, 2, 3),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(6),
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(6),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    trailBar: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        flexShrink: 0,
    },
}))

const recordsPersonPathFromSearch = (userId: number, search: string) => ({
    pathname: recordsPersonPath(userId),
    search: mergeRecordsSearch(search, {}, [
        'tab',
        'category',
        'open',
        'kinds',
    ]),
})

const inspectionPath = (inspectionId: number) =>
    Routes.apartment.inspection.detail.replace(':id', String(inspectionId))

const eventMatchesTab = (event: RecordsTimelineEvent, tab: UnitFileTab) => {
    if (tab === 'timeline') return event.kind !== 'lease'
    const kinds = KIND_FOR_TAB[tab]
    if (kinds == null) return true
    return kinds.includes(event.kind as RecordsTimelineKind)
}

const emptyCopy = (tab: UnitFileTab) => {
    if (tab === 'leases') return 'No leases on this unit.'
    if (tab === 'requests') return 'No service requests on this unit.'
    if (tab === 'inspections') return 'No inspections on this unit.'
    if (tab === 'work') return 'No workorders on this unit.'
    return 'Nothing on the audit trail for this unit yet.'
}

export const UnitFile = ({ unitId }: UnitFileProps) => {
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation()
    const { formatDate } = useTimezone()
    const search = parseRecordsSearch(location.search)
    const tab = parseUnitFileTab(search.tab)
    const selectedKinds = parseKindKeys(search.kinds)

    const setTab = (next: UnitFileTab) => {
        history.replace({
            pathname: location.pathname,
            search: mergeRecordsSearch(
                location.search,
                { tab: next },
                next === 'timeline' ? [] : ['kinds'],
            ),
        })
    }

    const setKinds = (next: string[]) => {
        history.replace({
            pathname: location.pathname,
            search: mergeRecordsSearch(location.search, {
                kinds: serializeKindKeys(parseKindKeys(next.join(','))),
            }),
        })
    }

    const fileQuery = useRecordsUnitFile(unitId)
    const timelineQuery = useRecordsUnitTimeline(unitId)
    const unit = fileQuery.data
    const tabEvents = (timelineQuery.data?.results ?? []).filter((event) =>
        eventMatchesTab(event, tab),
    )
    const events =
        tab === 'timeline'
            ? tabEvents.filter((event) =>
                  eventMatchesKindKeys(event.kind, selectedKinds),
              )
            : tabEvents
    const futureOccupants = unit?.future_occupants ?? []
    const loading = fileQuery.isLoading || timelineQuery.isLoading
    const missing = fileQuery.isError || timelineQuery.isError

    const goPerson = (userId: number) => {
        history.push(recordsPersonPathFromSearch(userId, location.search))
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                {unit ? (
                    <>
                        <Typography variant="h5" className={classes.unitName}>
                            {unit.name}
                        </Typography>
                        {unit.folder_path ? (
                            <Typography
                                variant="body2"
                                className={classes.unitPath}
                            >
                                {unit.folder_path}
                            </Typography>
                        ) : null}
                        <Typography
                            variant="body2"
                            className={classes.unitMeta}
                        >
                            {[
                                unit.occupied ? 'Occupied' : 'Vacant',
                                `${
                                    unit.open_service_request_count
                                } open request${
                                    unit.open_service_request_count === 1
                                        ? ''
                                        : 's'
                                }`,
                                unit.last_work_at
                                    ? `Last work ${formatDate(
                                          unit.last_work_at,
                                          'date-only',
                                      )}`
                                    : 'No work on file',
                            ].join(' · ')}
                        </Typography>
                        {unit.occupants.length > 0 ||
                        futureOccupants.length > 0 ? (
                            <div className={classes.occupants}>
                                {unit.occupants.map((occupant) => (
                                    <OccupantChip
                                        key={`${occupant.lease_id}-${occupant.area_id}`}
                                        occupant={occupant}
                                        onOpen={goPerson}
                                    />
                                ))}
                                {futureOccupants.map((occupant) => (
                                    <OccupantChip
                                        key={`future-${occupant.lease_id}-${occupant.area_id}`}
                                        occupant={occupant}
                                        prefix="Future"
                                        onOpen={goPerson}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Typography
                                variant="body2"
                                className={classes.unitMeta}
                            >
                                No current occupants
                            </Typography>
                        )}
                    </>
                ) : missing ? (
                    <>
                        <Typography variant="h5" className={classes.unitName}>
                            Unit not found
                        </Typography>
                        <Typography
                            variant="body2"
                            className={classes.unitMeta}
                        >
                            This unit is not on this property.
                        </Typography>
                    </>
                ) : (
                    <Typography variant="h5" className={classes.unitName}>
                        Unit File
                    </Typography>
                )}
            </div>
            <Tabs
                className={classes.tabs}
                value={tab}
                onChange={(_event, next: UnitFileTab) => setTab(next)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
            >
                <Tab
                    className={classes.tab}
                    value="timeline"
                    label="Audit Trail"
                />
                <Tab className={classes.tab} value="leases" label="Leases" />
                <Tab
                    className={classes.tab}
                    value="requests"
                    label="Service Requests"
                />
                <Tab
                    className={classes.tab}
                    value="inspections"
                    label="Inspections"
                />
                <Tab className={classes.tab} value="work" label="Workorder" />
            </Tabs>
            {tab === 'timeline' && !missing ? (
                <div className={classes.trailBar}>
                    {!loading ? (
                        <TrailKindFilter
                            eventKinds={tabEvents.map((event) => event.kind)}
                            selected={selectedKinds}
                            onChange={setKinds}
                        />
                    ) : null}
                    <TrailExport
                        unitId={unitId}
                        kinds={serializeAuditExportKinds(selectedKinds)}
                    />
                </div>
            ) : null}
            <div className={classes.listWrap}>
                {loading ? (
                    <div className={classes.loading}>
                        <CircularProgress size={28} />
                    </div>
                ) : missing ? (
                    <div className={classes.empty}>
                        Choose another unit from the list.
                    </div>
                ) : events.length === 0 ? (
                    <div className={classes.empty}>
                        {tab === 'timeline' && selectedKinds.length > 0
                            ? 'No events of this kind.'
                            : emptyCopy(tab)}
                    </div>
                ) : (
                    <TrailEventList>
                        {events.map((event) => (
                            <TrailEventCard
                                key={`${event.kind}-${event.source_id}-${event.at}`}
                                event={event}
                                onClick={
                                    event.kind === 'inspection' &&
                                    event.source_id != null
                                        ? () =>
                                              history.push(
                                                  inspectionPath(
                                                      event.source_id,
                                                  ),
                                              )
                                        : undefined
                                }
                            />
                        ))}
                    </TrailEventList>
                )}
            </div>
        </div>
    )
}

const OccupantChip = ({
    occupant,
    prefix,
    onOpen,
}: {
    occupant: RecordsUnitOccupant
    prefix?: string
    onOpen: (userId: number) => void
}) => {
    const name = occupant.name || 'Resident'
    const label = occupant.area_label
        ? `${name} · ${occupant.area_label}`
        : name
    return (
        <Chip
            size="small"
            clickable={occupant.user_id != null}
            label={prefix ? `${prefix} · ${label}` : label}
            onClick={() => {
                if (occupant.user_id != null) {
                    onOpen(occupant.user_id)
                }
            }}
            style={{ fontWeight: 600 }}
        />
    )
}
